// https://dpn-dashboard.datanext.nl/JourneyBuilder/GetSankeyData/7bd6658e-feb7-42cb-88d7-5ad68978224a
//let data = fetch('./data.json');

import {SVG} from '@svgdotjs/svg.js';
import { makeAsync, debounce, createValueRef, wrapAsModificationAwareProxy } from './helpers';
import { renderRaster, renderTouchPoints } from "@modules/JourneyBuilder/Components/Charts/SankeyV2/chart-element-renderers";
import { renderNodeHighlights, renderSelectedNode, centerNode } from "@modules/JourneyBuilder/Components/Charts/SankeyV2/chart-highlights";
import { renderTypeLegend, renderTouchpointLegend, renderDepthAlterationControls } from "@modules/JourneyBuilder/Components/Charts/SankeyV2/chart-controls";
import { calculateTypeHeights, calculateTypeYStartPositions, calculatePositionInfoPerNode } from "@modules/JourneyBuilder/Components/Charts/SankeyV2/chart-positioning";
import { buildTree } from "@modules/JourneyBuilder/Components/Charts/SankeyV2/chart-tree";
import {NODE_TYPES, NODE_DIMENSIONS, SPACING} from "@modules/JourneyBuilder/Components/Charts/SankeyV2/constants";


/**
 * Draw a sankey using the given data
 * @param {HTMLElement} element
 * @param {Object} sankeyData
 * @param {JourneyBuilder.Sankey.SankeyNode[]} sankeyData.nodes
 * @param {JourneyBuilder.Sankey.SankeyType[]} sankeyData.types
 * @param {SankeyOptions} [options]
 * @returns {Promise<Object>}
 */
export default async function(element, sankeyData, options) {
    options = {
        onNodeSelected: () => {},
        ...(options || {})
    }
    
    const {types, nodes: data} = sankeyData;

    /** @type {JourneyBuilder.Sankey.SankeyState} */
    let state = {
        stripeBackgroundId: '',
        highlightMode: createValueRef(undefined),
        selectedBrickId: createValueRef(undefined),
    }

    await makeAsync(() => initializeChartElements(element, options, state));
    await updateStateForDataset(state, data, types);
    await renderChartContent(state);
    await renderChartControls(state);
    
    state.selectedBrickId.subscribe((value) => {
        renderSelectedNode(state.svg, state.tree, value);
        options.onNodeSelected(value);
    });
    
    return {
        destroy(){
            //Allow SVGJs to do potential additional cleanup
            state.svg.remove();
            
            [...element.childNodes].forEach(node => node.remove());
            
            const svgJsElements = [...document.querySelectorAll('svg')]
                .filter(x => !!x.attributes["xmlns:svgjs"]);
            
            //https://svgjs.com/docs/3.1/faq/#two-svg-s-are-generated-when-i-initialize-svg-js-is-that-a-bug
            //If only a single SVGJS element remains; it is assumed to be the 'parser' element
            //it should be safe to clean this element
            if(svgJsElements.length === 1){
                svgJsElements[0].remove();
            }
        },
        
        setHighlightMode(mode){
            return state.highlightMode.value = mode;
        },
        
        /**
         * Select the nodes with the given brick id
         * @param {string} [brickId] id of the brick to select
         */
        selectBrick(brickId){
            state.selectedBrickId.value = brickId;
        },

        /**
         * Center the first node with the given brick id
         * @param {string} [brickId] id of the brick to scroll to
         */
        centerBrick(brickId){
            /** @type {JourneyBuilder.Sankey.TreeBranchV2 | undefined} */
            let highlightedBranch = undefined;
            
            //Attempt to find the first branch for the given brick
            state.renderedTree.forAllBranches(branch => {
                if(branch.node.brickId !== brickId){
                    return;
                }
                
                if(highlightedBranch && branch.level > highlightedBranch.level){
                    return;
                }
                
                highlightedBranch = branch;
            });
            
            if(highlightedBranch){
                centerNode(state.svg, highlightedBranch.node.id);   
            }
        },

        /**
         * Redraw the sankey chart with the given new dataset
         * @param {Object} newSankeyData
         * @param {JourneyBuilder.Sankey.SankeyNode[]} newSankeyData.nodes
         * @param {JourneyBuilder.Sankey.SankeyType[]} newSankeyData.types
         */
        async setData(newSankeyData) {
            const { nodes: data, types } = newSankeyData;

            await updateStateForDataset(state, data, types);
            await renderChartContent(state);
            await renderChartControls(state);
        }
    }
}

/**
 * Prepare the given element for a Sankey Graph to be drawn on it
 * @param {HTMLElement} element
 * @param {SankeyOptions} options
 * @param {JourneyBuilder.Sankey.SankeyState} state
 */
function initializeChartElements(element, options, state){
    //Start with a clean slate
    Array.from(element.children).forEach(childNode => childNode.remove());

    const typeLegend = element.appendChild(document.createElement('div'));

    //Invoking addTo with the result of a document.createElement(...) call returns the wrong implementation
    const svg = SVG().addTo(element);

    const touchpointLegend = element.appendChild(document.createElement('div'));
    const depthAlterationControls = element.appendChild(document.createElement('div'));

    typeLegend.classList.add('type-legend');
    touchpointLegend.classList.add('touchpoint-legend');
    depthAlterationControls.classList.add('depth-controls');

    svg.on('click', ev => {
        var target = ev.target.closest('[data-nodeid]');
        if(!target) return;
        ev.preventDefault();
        
        
        const brickId = state.tree.getBranchByNodeId(target.dataset.nodeid).node.brickId;

        //Deselect on selecting the same node twice
        state.selectedBrickId.value = state.selectedBrickId.value === brickId ?
            null :
            brickId;
    });

    state.svg = svg;
    state.typeLegend = typeLegend;
    state.touchpointLegend = touchpointLegend;
    state.depthAlterationControls = depthAlterationControls;
}

/**
 * Update the chart state with a new dataset, state dependent refs will be re-created
 * @param {JourneyBuilder.Sankey.SankeyState} state
 * @param {JourneyBuilder.Sankey.SankeyNode[]} data
 * @param {JourneyBuilder.Sankey.SankeyType[]} types
 * @return {Promise<void>}
 */
async function updateStateForDataset(state, data, types) {
    const tree = buildTree(data);

    const dataTypes = data.reduce((types, node) => {
        //Goal nodes are not included in the regular flow - goal nodes belong to the 'goal' type
        if(!node.isGoal && !types.includes(node.type)){
            if(node.type === NODE_TYPES.AUDIENCE){
                types.unshift(node.type);
            } else {
                types.push(node.type);
            }
        }

        return types;
    }, []);

    let maxAmount = 0;
    data.forEach(x => maxAmount = Math.max(maxAmount, x.total));

    const typeYStartPositions = await makeAsync(() => calculateTypeYStartPositions(tree, dataTypes, maxAmount));
    const typeHeights = await makeAsync(() => calculateTypeHeights(tree, maxAmount));
    const displayedTypesValue = await makeAsync(() => types.map(t => t.name));

    state.tree = tree;
    state.renderedTree = tree;
    state.maxAmount = maxAmount;
    state.typeYStartPositions = typeYStartPositions;
    state.typeHeights = typeHeights;
    state.data = data;
    state.types = dataTypes;
    state.hasGoalNodes = data.some(n => n.isGoal);

    state.displayedDepth = createValueRef(calculateFilterableLevels(state));
    state.highlightMode = createValueRef(undefined);

    //Automagically re-render chart upon selecting/deselecting a specific type
    state.displayedTypes = wrapAsModificationAwareProxy(
        displayedTypesValue,
        async () => {
            await updateStateRenderedTree(state);
            await renderChartContent(state);
        }
    );

    //Debounce for .25 seconds to allow multiple alterations without actively hindering
    state.displayedDepth.subscribe(debounce((newValue, oldValue) => {
        //Value is being initialized; ignore event
        if(oldValue === -1){
            return;
        }

        //Guard against nothing from being displayed/more being 'displayed' than possible
        newValue = Math.max(0, newValue);
        newValue = Math.min(newValue, calculateFilterableLevels(state));

        state.displayedDepth.setValueDirect(newValue);

        if(newValue !== oldValue){
            //Known race condition, execution is expected to be fast, will most likely not result in actual problems
            updateStateRenderedTree(state)
                .then(() => renderChartContent(state));
        }
    }, 250));

    state.highlightMode.subscribe((value) => renderNodeHighlights(state.svg, state.renderedTree, value));
}

/**
 * Rebuild the actual rendered tree used for state based rendering
 * @param {JourneyBuilder.Sankey.SankeyState} state
 * @return {Promise<void>}
 */
async function updateStateRenderedTree(state){
    state.renderedTree = await makeAsync(() => {
        return state.tree.filter(branch => {
            //Goal nodes are never filtered
            if(branch.node.isGoal){
                return false;
            }

            //return !state.displayedTypes.includes(branch.node.type);
            return branch.level > state.displayedDepth.value ||
                !state.displayedTypes.includes(branch.node.type);
        })
    });
}

/**
 * Calculate how many levels may actually be filtered
 * 
 * @param {JourneyBuilder.Sankey.SankeyState} state
 * @return {Number}
 */
function calculateFilterableLevels(state) {
    //Goal nodes can never be filtered and are always placed at the end
    //goal nodes may thus be ignored when it comes to filtering by level
    return state.tree.totalLevels - (state.hasGoalNodes ? 1 : 0);
}

/**
 * Render the content of the Sankey chart based on the current chart state
 * @param {JourneyBuilder.Sankey.SankeyState} state
 * @return {Promise<void>}
 */
async function renderChartContent(state){
    const maxAmount = state.maxAmount;
    const svg = state.svg;
    const typeYStartPositions = state.typeYStartPositions;
    const typeHeights = state.typeHeights;
    const renderedTree = state.renderedTree;
    
    const positionInfoPerNode = calculatePositionInfoPerNode(renderedTree, typeYStartPositions, maxAmount);
    
    let totalHeight = Object.values(typeHeights)
      .reduce((totalHeight, typeHeight) => totalHeight + typeHeight + SPACING.VERTICAL, 0);

    //Not all nodes fit into a specific type; eg. goal nodes are stacked at the very end grouped by brickId
    //this may result in our goal brick stack having a greater height than the sum of all types
    positionInfoPerNode.forEach(pI => totalHeight = Math.max(totalHeight, pI.y + pI.elementHeight));
    
    //SPACING.HORIZONTAL only indicates horizontal starting points, include our final node set by adding the node width
    const totalWidth = (state.tree.totalLevels * SPACING.HORIZONTAL) + NODE_DIMENSIONS.WIDTH;

    svg.node.style.minHeight = totalHeight + 'px';
    svg.node.style.minWidth = totalWidth + 'px';

    //Clear our elements in case positioning changes
    svg.clear();

    // TODO: render per level, so we go step by step
    await makeAsync(() => renderTouchPoints(svg, renderedTree, positionInfoPerNode));
    await makeAsync(() => renderRaster(svg, typeYStartPositions));
    await makeAsync(() => renderNodeHighlights(svg, renderedTree, state.highlightMode.value));
    await makeAsync(() => renderSelectedNode(svg, renderedTree, state.selectedBrickId.value));
}

/**
 * Render chart controls
 * @param {JourneyBuilder.Sankey.SankeyState} state
 * @return {Promise<void>}
 */
async function renderChartControls(state) {
    const emptyElement = element => [...element.childNodes].forEach(node => node.remove());

    emptyElement(state.typeLegend);
    emptyElement(state.touchpointLegend);
    emptyElement(state.depthAlterationControls);

    //TODO: Move to separate method -> only invoke once/dataset
    await makeAsync(() => renderTypeLegend(state.typeLegend, state.types, state.typeHeights, state.typeYStartPositions, state.displayedTypes));
    await makeAsync(() => renderTouchpointLegend(state.touchpointLegend, state.renderedTree.totalLevels));
    
    //Goal nodes aren't included in depth alteration
    await makeAsync(() => renderDepthAlterationControls(state.depthAlterationControls, state.displayedDepth, calculateFilterableLevels(state)));
}
